import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getMovieById, getRelatedMovies, getTypeLabel } from "@/utils/movie";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EpisodeList from "./components/EpisodeList";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [currentEpisode, setCurrentEpisode] = useState(1);
  const [toast, setToast] = useState("");

  usePageMeta(
    movie
      ? `${movie.title} - 秋霞网影视详情及在线观看`
      : "影视资源不存在 - 秋霞网",
    movie
      ? `秋霞网为您提供${movie.title}的详细影视信息，类型：${movie.genres.join("、")}，地区：${movie.region}，年份：${movie.year}，评分 ${movie.rating}。`
      : "您访问的影视资源不存在或已下架。"
  );

  useEffect(() => {
    setCurrentEpisode(1);
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-film-line text-6xl text-foreground-400" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          影视资源不存在或已下架
        </h1>
        <p className="mt-3 text-sm text-foreground-500">
          您访问的影视资源可能已被移除，请返回首页浏览其他内容
        </p>
        <Link
          to="/"
          className="mt-7 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 5);
  const categoryLabel = getTypeLabel(movie.type);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-500 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category/${movie.type}`}
          className="transition-colors hover:text-primary-500 cursor-pointer"
        >
          {categoryLabel}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-44 sm:w-52 lg:mx-0 lg:w-full">
          <div className="overflow-hidden rounded-xl border border-background-300/60">
            <img
              src={movie.poster}
              alt={movie.title}
              title={`${movie.title} 秋霞网影视详情`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                toggleFavorite(movie.id);
                setToast(favorite ? "已取消收藏" : "已加入收藏");
              }}
              className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md py-2.5 text-sm transition-colors cursor-pointer ${
                favorite
                  ? "bg-primary-500 font-medium text-foreground-950"
                  : "border border-background-300 text-foreground-900 hover:border-primary-400"
              }`}
            >
              <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
              {favorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-900 transition-colors hover:border-primary-400 cursor-pointer"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
          </div>
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {movie.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs text-foreground-950">
              {categoryLabel}
            </span>
            <span className="flex items-center gap-1 font-medium text-accent-400">
              <i className="ri-star-fill text-xs" />
              {movie.rating.toFixed(1)} 分
            </span>
            <span>{movie.year}</span>
            <span>{movie.region}</span>
            <span>{movie.language}</span>
            {movie.type === "tv" && <span>全 {movie.episodes} 集</span>}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <span className="shrink-0 text-foreground-500">导演：</span>
              <span className="text-foreground-900">{movie.director}</span>
            </div>
            <div className="flex gap-2">
              <span className="shrink-0 text-foreground-500">更新时间：</span>
              <span className="text-foreground-900">{movie.updateTime}</span>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <span className="shrink-0 text-foreground-500">主演：</span>
              <span className="text-foreground-900">{movie.actors.join(" / ")}</span>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <span className="shrink-0 text-foreground-500">类型：</span>
              <span className="text-foreground-900">{movie.genres.join(" / ")}</span>
            </div>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-foreground-600">
            {movie.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById("play-section");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </button>
            <Link
              to={`/category/${movie.type}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-900 transition-colors hover:border-primary-400 cursor-pointer"
            >
              <i className="ri-apps-2-line" />
              更多{categoryLabel}
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {movie.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-secondary-700 px-3 py-1 text-xs text-foreground-500"
              >
                # {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <section id="play-section" className="mt-12 scroll-mt-24">
        <SectionHeading
          title={movie.type === "tv" ? "选集播放" : "在线观看"}
          desc="本站为演示站点，点击剧集可模拟切换章节，暂不支持真实播放"
        />

        <div className="overflow-hidden rounded-xl border border-background-300/60 bg-background-100">
          <div className="relative flex aspect-video w-full flex-col items-center justify-center bg-secondary-950">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary-950 via-secondary-900 to-secondary-950" />
            <button
              type="button"
              onClick={() =>
                setToast(
                  movie.type === "tv"
                    ? `正在模拟播放 第 ${currentEpisode} 集（演示，无真实播放）`
                    : "正在模拟播放 正片（演示，无真实播放）"
                )
              }
              className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-foreground-950 transition-transform hover:scale-105 cursor-pointer md:h-20 md:w-20"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </button>
            <p className="relative z-10 mt-4 text-sm text-foreground-500">
              {movie.type === "tv"
                ? `第 ${currentEpisode} 集`
                : "正片 HD 高清"}
              {"  ·  "}
              秋霞网演示播放器
            </p>
          </div>

          <div className="border-t border-background-300/60 p-4 md:p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                <i className="ri-list-check-2 text-primary-500" />
                {movie.type === "tv" ? `剧集列表（全 ${movie.episodes} 集）` : "播放源"}
              </span>
              <span className="hidden text-xs text-foreground-500 sm:block">
                提示：滑动可快速切换剧集
              </span>
            </div>
            <EpisodeList
              movie={movie}
              current={currentEpisode}
              onSelect={(ep) => {
                setCurrentEpisode(ep);
                setToast(
                  movie.type === "tv"
                    ? `已切换至 第 ${ep} 集（演示）`
                    : "已选择 正片（演示）"
                );
              }}
            />
          </div>
        </div>
      </section>

      <section className="mt-12">
        <SectionHeading title="剧情简介" />
        <div className="rounded-xl border border-background-300/60 bg-background-100 p-5 md:p-6">
          <p className="text-sm leading-loose text-foreground-600">
            {movie.synopsis}
          </p>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-background-300/60 pt-5">
            <Link
              to={`/category/${movie.type}`}
              className="rounded-md bg-secondary-700 px-3 py-1.5 text-xs text-primary-400 transition-colors hover:bg-secondary-600 cursor-pointer"
            >
              {categoryLabel}频道
            </Link>
            {movie.genres.map((g) => (
              <Link
                key={g}
                to={`/category/${movie.type}`}
                className="rounded-md bg-secondary-700 px-3 py-1.5 text-xs text-foreground-500 transition-colors hover:bg-secondary-600 cursor-pointer"
              >
                {g}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-12">
        <SectionHeading
          title="相关推荐"
          desc="根据类型与标签为您推荐"
          actionText="查看更多"
          onAction={() => navigate(`/category/${movie.type}`)}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {related.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 animate-fade-in">
          <div className="flex items-center gap-2 rounded-md bg-secondary-950 px-5 py-3 text-sm text-foreground-900 shadow-lg ring-1 ring-background-300/60">
            <i className="ri-information-line text-primary-500" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}